import type { Admission, AdmissionDetailsValues, AdmissionCode } from '../../types'
import type { Response } from '../admission-grid-2'
import type { UseMutationResult } from '@tanstack/react-query'

import { useMutation, useQueryClient } from '@tanstack/react-query'
// @ts-expect-error // it's okay
import Admissions from 'shared_src/dictionaries/encounters/AdmissionCodes'

import http from '../../../../../../../lib/http'
import { useInvalidateIGAO } from '../../../../../../igao/hooks/invalidate-igao'

interface Props {
  patientId?: string
  isEmpty?: boolean
  variables?: AdmissionDetailsValues
}

export function useCreateAdmissionMutation ({ isEmpty, patientId }: Props): UseMutationResult<unknown, unknown, AdmissionDetailsValues> {
  const queryClient = useQueryClient()
  let invalidateIGAO = useInvalidateIGAO('admissions')

  const admissionCodesOptions: AdmissionCode[] = (Admissions as Array<{code: string, label: string}>).map(({ code, label }) => {
    return {
      value: code,
      label
    }
  })

  return useMutation<unknown, unknown, AdmissionDetailsValues>({
    async mutationFn (value) {
      if (!patientId) {
        return
      }

      const promise: Promise<unknown> = isEmpty
        ? http.putRequest(`/patients/${patientId}/admissions`, { empty: false })
        : Promise.resolve()

      return await promise.then(async () => await http.postRequest(`/patients/${patientId}/admission`, { value }))
    },
    async onMutate (variables) {
      const newAdmission: Admission = {
        resourceType: 'Encounter',
        status: 'finished',
        facility: variables.facility,
        id: `local-${Date.now()}`,
        participant: {
          coding: [
            {
              system: 'http://hl7.org/fhir/v3/ParticipationType',
              code: 'ADM',
              display: 'admitter'
            }
          ]
        },
        physician: variables.physician,
        anticipatedDischargeDate: variables.anticipatedDischargeDate,
        admissionDate: variables.admissionDate,
        reason: variables.reason,
        dischargeDate: variables.dischargeDate,
        type: {
          code: variables.admissionCode ?? '',
          system: '',
          display: admissionCodesOptions.find((a) => a.value === variables.admissionCode)?.label ?? ''
        }
      }

      queryClient.setQueriesData<Response>(
        { queryKey: [ 'admissions-grid', patientId as string ] },
        (current) => {
          return {
            ...current,
            empty: current?.empty ?? false,
            entries: [
              ...(current?.entries ?? []),
              newAdmission
            ]
          }
        })
    },
    async onSettled () {
      await queryClient.invalidateQueries({ queryKey: [ 'admissions-grid', patientId as string ] })
      await invalidateIGAO()
      await queryClient.invalidateQueries({
        predicate ({ queryKey }) {
          return Array.isArray(queryKey) && queryKey[0] === 'claims-history/admissions'
        }
      })
    },
    async onError () {
      void queryClient.invalidateQueries({ queryKey: [ 'admissions-grid', patientId as string ] })
    }
  })
}
